import { Side } from '@/connect';
import { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';
import Decimal from 'decimal.js';

export function enumValueFormatter<EnumType extends Record<number, string>>(
  enumType: EnumType,
  useForExport = true
): Pick<ColDef, 'valueFormatter' | 'useValueFormatterForExport'> {
  return {
    valueFormatter: (params: ValueFormatterParams): string => {
      return enumType[params.value];
    },
    useValueFormatterForExport: useForExport,
  };
}

export function formatDecimal(
  input: Decimal | string | null | undefined,
  decimalPlaces = 2,
  dashWhenUndefined = true
): string {
  let value = typeof input === 'string' ? new Decimal(input) : input;

  if (!value && dashWhenUndefined) {
    return '–';
  }
  if (!value) {
    value = new Decimal(0);
  }

  return value.toFixed(decimalPlaces);
}

export function decimalValueFormatter<Data, Value extends Decimal | string | undefined>(
  decimalPlaces = 2,
  dashWhenUndefined = true,
  useForExport = false
): Pick<ColDef<Data, Value>, 'valueFormatter' | 'useValueFormatterForExport'> {
  return {
    valueFormatter: (params: ValueFormatterParams<Data, Value>): string => {
      return formatDecimal(params.value, decimalPlaces, dashWhenUndefined);
    },
    useValueFormatterForExport: useForExport,
  };
}

export function formatDecimalPercentage(
  input: Decimal | string | null | undefined,
  decimalPlaces = 2,
  dashWhenUndefined = true
): string {
  let value = typeof input === 'string' ? new Decimal(input) : input;

  if (!value && dashWhenUndefined) {
    return '–';
  }
  if (!value) {
    value = new Decimal(0);
  }

  return value.toFixed(decimalPlaces) + '%';
}

export function decimalPercentageValueFormatter<Data, Value extends Decimal | string | undefined>(
  decimalPlaces = 0,
  dashWhenUndefined = true,
  useForExport = true
): Pick<ColDef<Data, Value>, 'valueFormatter' | 'useValueFormatterForExport'> {
  return {
    valueFormatter: (params: ValueFormatterParams<Data, Value>): string => {
      return formatDecimalPercentage(params.value, decimalPlaces, dashWhenUndefined);
    },
    useValueFormatterForExport: useForExport,
  };
}

export function decimalIndependentAmountRateAsPercentCollateralValueFormatter<
  Data,
  Value extends Decimal | string | undefined,
>(
  decimalPlaces = 0,
  dashWhenUndefined = true,
  useForExport = true
): Pick<ColDef<Data, Value>, 'valueFormatter' | 'useValueFormatterForExport'> {
  return {
    valueFormatter: (params: ValueFormatterParams<Data, Value>): string => {
      let value = typeof params.value === 'string' ? new Decimal(params.value) : params.value;

      if (!value && dashWhenUndefined) {
        return '–';
      }
      if (!value) {
        value = new Decimal(0);
      }

      return formatDecimalPercentage(value as Decimal, decimalPlaces);
    },
    useValueFormatterForExport: useForExport,
  };
}

export function formatSide(side: Side): string {
  return side === Side.BORROW ? 'BORROW' : 'LOAN';
}
